// Minimal ArcGIS Arcade interpreter for renderer `valueExpression` strings.
//
// esri-leaflet-renderers classifies features by a literal attribute `field`; it has no
// Arcade engine, so services whose renderer uses a `valueExpression` (an Arcade script)
// render with no symbology. This module evaluates the COMMON, classification-style subset
// of Arcade — enough to drive uniqueValue/classBreaks renderers — without `eval`/`new
// Function` (blocked by the app CSP) and without the heavyweight @arcgis/core SDK.
//
// `compileArcade(text)` returns `{ evaluate(props) }`, or `null` if the expression uses
// anything outside the supported subset (the caller then falls back to default symbology).
// Supported: `$feature.X` / `$feature["X"]`, `var` bindings, numeric/string/bool/null
// literals, comparison (== != < <= > >=), logical (&& || ! and/or/not), arithmetic
// (+ - * / %, string `+` concat), `if/else` + `return`, an implicit trailing-expression
// return, and the builtins `When` / `IIf` / `Decode`. Anything else -> null.

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

const TWO_CHAR_OPS = ["==", "!=", "<=", ">=", "&&", "||"];
const SINGLE_OPS = ["<", ">", "+", "-", "*", "/", "%", "!"];

function isIdentStart(ch) {
	return /[A-Za-z_$]/.test(ch);
}

function isIdentPart(ch) {
	return /[A-Za-z0-9_$]/.test(ch);
}

function isDigit(ch) {
	return ch >= "0" && ch <= "9";
}

function tokenize(text) {
	const tokens = [];
	let i = 0;
	const n = text.length;

	while (i < n) {
		const ch = text[i];

		// Whitespace (not newline) and carriage returns.
		if (ch === " " || ch === "\t" || ch === "\r") {
			i++;
			continue;
		}

		// Newline — a potential statement separator.
		if (ch === "\n") {
			tokens.push({ type: "nl" });
			i++;
			continue;
		}

		// Line comment.
		if (ch === "/" && text[i + 1] === "/") {
			while (i < n && text[i] !== "\n") {
				i++;
			}
			continue;
		}

		// String literal (single or double quoted).
		if (ch === "'" || ch === '"') {
			const quote = ch;
			let value = "";
			i++;
			while (i < n && text[i] !== quote) {
				if (text[i] === "\\" && i + 1 < n) {
					const next = text[i + 1];
					value += next === "n" ? "\n" : next === "t" ? "\t" : next;
					i += 2;
					continue;
				}
				value += text[i];
				i++;
			}
			if (i >= n) {
				throw new Error("unterminated string");
			}
			i++; // closing quote
			tokens.push({ type: "str", value });
			continue;
		}

		// Number literal.
		if (isDigit(ch) || (ch === "." && isDigit(text[i + 1]))) {
			let raw = "";
			while (i < n && (isDigit(text[i]) || text[i] === ".")) {
				raw += text[i];
				i++;
			}
			tokens.push({ type: "num", value: Number(raw) });
			continue;
		}

		// Identifier / keyword.
		if (isIdentStart(ch)) {
			let name = "";
			while (i < n && isIdentPart(text[i])) {
				name += text[i];
				i++;
			}
			tokens.push({ type: "ident", value: name });
			continue;
		}

		// Two-character operators.
		const two = text.substr(i, 2);
		if (TWO_CHAR_OPS.includes(two)) {
			tokens.push({ type: "op", value: two });
			i += 2;
			continue;
		}

		// Single-character operators.
		if (SINGLE_OPS.includes(ch)) {
			tokens.push({ type: "op", value: ch });
			i++;
			continue;
		}

		// Structural punctuation.
		if (ch === "=") {
			tokens.push({ type: "assign" });
			i++;
			continue;
		}
		if ("()[]{},.;".includes(ch)) {
			tokens.push({ type: ch });
			i++;
			continue;
		}

		throw new Error(`unexpected character: ${ch}`);
	}

	tokens.push({ type: "eof" });
	return tokens;
}

// ---------------------------------------------------------------------------
// Parser (recursive descent with precedence climbing)
// ---------------------------------------------------------------------------

const BUILTINS = ["iif", "when", "decode"];

function parse(tokens) {
	let pos = 0;

	function peek() {
		return tokens[pos];
	}

	function next() {
		return tokens[pos++];
	}

	function check(type) {
		return peek().type === type;
	}

	function isOp(value) {
		const t = peek();
		return t.type === "op" && t.value === value;
	}

	function isKeyword(word) {
		const t = peek();
		return t.type === "ident" && t.value.toLowerCase() === word;
	}

	function skipNewlines() {
		while (check("nl")) {
			pos++;
		}
	}

	function skipSeparators() {
		while (check("nl") || check(";")) {
			pos++;
		}
	}

	function expect(type) {
		if (!check(type)) {
			throw new Error(`expected ${type} but found ${peek().type}`);
		}
		return next();
	}

	function parseProgram() {
		const body = [];
		skipSeparators();
		while (!check("eof")) {
			body.push(parseStatement());
			skipSeparators();
		}
		return { type: "Program", body };
	}

	function parseBlock() {
		expect("{");
		const body = [];
		skipSeparators();
		while (!check("}")) {
			if (check("eof")) {
				throw new Error("unterminated block");
			}
			body.push(parseStatement());
			skipSeparators();
		}
		next(); // consume }
		return body;
	}

	function parseStatement() {
		if (isKeyword("var")) {
			next();
			const name = expect("ident").value;
			expect("assign");
			skipNewlines();
			return { type: "VarDecl", name, expr: parseExpression() };
		}
		if (isKeyword("if")) {
			return parseIf();
		}
		if (isKeyword("return")) {
			next();
			skipNewlines();
			return { type: "Return", expr: parseExpression() };
		}
		return { type: "ExprStmt", expr: parseExpression() };
	}

	function parseIf() {
		next(); // if
		expect("(");
		skipNewlines();
		const cond = parseExpression();
		skipNewlines();
		expect(")");
		skipNewlines();
		const consequent = parseBlock();
		let alternate = null;

		const mark = pos;
		skipSeparators();
		if (isKeyword("else")) {
			next();
			skipNewlines();
			alternate = isKeyword("if") ? [parseIf()] : parseBlock();
		} else {
			pos = mark; // no else — leave separators for the caller
		}
		return { type: "If", cond, consequent, alternate };
	}

	// Precedence climbing: or < and < equality < comparison < additive < multiplicative < unary.

	function parseExpression() {
		return parseOr();
	}

	function parseOr() {
		let left = parseAnd();
		while (isOp("||") || isKeyword("or")) {
			next();
			skipNewlines();
			left = { type: "Logical", op: "||", left, right: parseAnd() };
		}
		return left;
	}

	function parseAnd() {
		let left = parseEquality();
		while (isOp("&&") || isKeyword("and")) {
			next();
			skipNewlines();
			left = { type: "Logical", op: "&&", left, right: parseEquality() };
		}
		return left;
	}

	function parseEquality() {
		let left = parseComparison();
		while (isOp("==") || isOp("!=")) {
			const op = next().value;
			skipNewlines();
			left = { type: "Binary", op, left, right: parseComparison() };
		}
		return left;
	}

	function parseComparison() {
		let left = parseAdditive();
		while (isOp("<") || isOp("<=") || isOp(">") || isOp(">=")) {
			const op = next().value;
			skipNewlines();
			left = { type: "Binary", op, left, right: parseAdditive() };
		}
		return left;
	}

	function parseAdditive() {
		let left = parseMultiplicative();
		while (isOp("+") || isOp("-")) {
			const op = next().value;
			skipNewlines();
			left = { type: "Binary", op, left, right: parseMultiplicative() };
		}
		return left;
	}

	function parseMultiplicative() {
		let left = parseUnary();
		while (isOp("*") || isOp("/") || isOp("%")) {
			const op = next().value;
			skipNewlines();
			left = { type: "Binary", op, left, right: parseUnary() };
		}
		return left;
	}

	function parseUnary() {
		if (isOp("!") || isOp("-")) {
			const op = next().value;
			skipNewlines();
			return { type: "Unary", op, operand: parseUnary() };
		}
		if (isKeyword("not")) {
			next();
			skipNewlines();
			return { type: "Unary", op: "!", operand: parseUnary() };
		}
		return parsePostfix();
	}

	function parsePostfix() {
		let node = parsePrimary();
		while (true) {
			if (check(".")) {
				next();
				const name = expect("ident").value;
				node = { type: "Member", object: node, property: name, computed: null };
			} else if (check("[")) {
				next();
				skipNewlines();
				const index = parseExpression();
				skipNewlines();
				expect("]");
				node = { type: "Member", object: node, property: null, computed: index };
			} else if (check("(")) {
				node = { type: "Call", callee: node, args: parseArguments() };
			} else {
				break;
			}
		}
		return node;
	}

	function parseArguments() {
		expect("(");
		skipNewlines();
		const args = [];
		if (!check(")")) {
			while (true) {
				args.push(parseExpression());
				skipNewlines();
				if (check(",")) {
					next();
					skipNewlines();
					continue;
				}
				break;
			}
		}
		expect(")");
		return args;
	}

	function parsePrimary() {
		const t = peek();
		if (t.type === "num") {
			next();
			return { type: "Literal", value: t.value };
		}
		if (t.type === "str") {
			next();
			return { type: "Literal", value: t.value };
		}
		if (t.type === "ident") {
			const lower = t.value.toLowerCase();
			if (lower === "true") {
				next();
				return { type: "Literal", value: true };
			}
			if (lower === "false") {
				next();
				return { type: "Literal", value: false };
			}
			if (lower === "null") {
				next();
				return { type: "Literal", value: null };
			}
			next();
			return { type: "Var", name: t.value };
		}
		if (t.type === "(") {
			next();
			skipNewlines();
			const expr = parseExpression();
			skipNewlines();
			expect(")");
			return expr;
		}
		throw new Error(`unexpected token: ${t.type}`);
	}

	const ast = parseProgram();
	if (!check("eof")) {
		throw new Error("trailing tokens");
	}
	return ast;
}

// ---------------------------------------------------------------------------
// Validation — reject anything outside the supported subset (throws -> null)
// ---------------------------------------------------------------------------

function validate(ast) {
	for (const stmt of ast.body) {
		validateStmt(stmt);
	}
}

function validateStmt(stmt) {
	switch (stmt.type) {
		case "VarDecl":
		case "Return":
		case "ExprStmt":
			validateExpr(stmt.expr);
			return;
		case "If":
			validateExpr(stmt.cond);
			stmt.consequent.forEach(validateStmt);
			if (stmt.alternate) {
				stmt.alternate.forEach(validateStmt);
			}
			return;
		default:
			throw new Error(`unsupported statement: ${stmt.type}`);
	}
}

function validateExpr(node) {
	switch (node.type) {
		case "Literal":
			return;
		case "Var":
			// Bare profile variables ($feature used directly, $map/$layer/$datastore, etc.)
			// are not supported — only `$feature.Field` member access is.
			if (node.name.startsWith("$")) {
				throw new Error(`unsupported variable: ${node.name}`);
			}
			return;
		case "Member":
			if (node.object.type !== "Var" || node.object.name.toLowerCase() !== "$feature") {
				throw new Error("only $feature member access is supported");
			}
			if (node.computed) {
				validateExpr(node.computed);
			}
			return;
		case "Unary":
			validateExpr(node.operand);
			return;
		case "Binary":
		case "Logical":
			validateExpr(node.left);
			validateExpr(node.right);
			return;
		case "Call": {
			if (node.callee.type !== "Var" || !BUILTINS.includes(node.callee.name.toLowerCase())) {
				throw new Error("unsupported function call");
			}
			const fn = node.callee.name.toLowerCase();
			const count = node.args.length;
			if (fn === "iif" && count !== 3) {
				throw new Error("IIf requires 3 arguments");
			}
			if (fn === "when" && (count < 3 || count % 2 === 0)) {
				throw new Error("When requires an odd argument count >= 3");
			}
			if (fn === "decode" && (count < 4 || count % 2 !== 0)) {
				throw new Error("Decode requires an even argument count >= 4");
			}
			node.args.forEach(validateExpr);
			return;
		}
		default:
			throw new Error(`unsupported expression: ${node.type}`);
	}
}

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

function truthy(value) {
	if (typeof value === "boolean") {
		return value;
	}
	if (value === null || value === undefined) {
		return false;
	}
	if (typeof value === "number") {
		return value !== 0 && !Number.isNaN(value);
	}
	if (typeof value === "string") {
		return value.length > 0;
	}
	return Boolean(value);
}

function execProgram(body, ctx) {
	const result = execBlock(body, ctx);
	return "value" in result ? result.value : undefined;
}

function execStatement(stmt, ctx) {
	switch (stmt.type) {
		case "VarDecl":
			ctx.vars[stmt.name.toLowerCase()] = evalExpr(stmt.expr, ctx);
			return {};
		case "Return":
			return { returned: true, value: evalExpr(stmt.expr, ctx) };
		case "ExprStmt":
			return { value: evalExpr(stmt.expr, ctx) };
		case "If": {
			if (truthy(evalExpr(stmt.cond, ctx))) {
				return execBlock(stmt.consequent, ctx);
			}
			if (stmt.alternate) {
				return execBlock(stmt.alternate, ctx);
			}
			return {};
		}
		default:
			return {};
	}
}

// A block evaluates to its last expression's value (Arcade block semantics), or propagates an early
// Return. execProgram (top level) and the If branches both run through here, so the program top level
// and nested blocks can't diverge on what "the block's value" is.
function execBlock(body, ctx) {
	let last = {};
	for (const stmt of body) {
		const result = execStatement(stmt, ctx);
		if (result.returned) {
			return result;
		}
		if ("value" in result) {
			last = result;
		}
	}
	return last;
}

function evalExpr(node, ctx) {
	switch (node.type) {
		case "Literal":
			return node.value;
		case "Var":
			return ctx.vars[node.name.toLowerCase()];
		case "Member": {
			const key = node.computed ? String(evalExpr(node.computed, ctx)) : node.property;
			return ctx.feature[key];
		}
		case "Unary": {
			const operand = evalExpr(node.operand, ctx);
			return node.op === "!" ? !truthy(operand) : -Number(operand);
		}
		case "Logical": {
			const left = evalExpr(node.left, ctx);
			if (node.op === "&&") {
				return truthy(left) ? truthy(evalExpr(node.right, ctx)) : false;
			}
			return truthy(left) ? true : truthy(evalExpr(node.right, ctx));
		}
		case "Binary":
			return evalBinary(node, ctx);
		case "Call":
			return evalCall(node, ctx);
		default:
			return undefined;
	}
}

function evalBinary(node, ctx) {
	const a = evalExpr(node.left, ctx);
	const b = evalExpr(node.right, ctx);
	switch (node.op) {
		case "==":
			return a == b; // eslint-disable-line eqeqeq -- Arcade-style loose equality
		case "!=":
			return a != b; // eslint-disable-line eqeqeq -- Arcade-style loose equality
		case "<":
			return a < b;
		case "<=":
			return a <= b;
		case ">":
			return a > b;
		case ">=":
			return a >= b;
		case "+":
			return typeof a === "string" || typeof b === "string" ? String(a) + String(b) : Number(a) + Number(b);
		case "-":
			return Number(a) - Number(b);
		case "*":
			return Number(a) * Number(b);
		case "/":
			return Number(a) / Number(b);
		case "%":
			return Number(a) % Number(b);
		default:
			return undefined;
	}
}

function evalCall(node, ctx) {
	const fn = node.callee.name.toLowerCase();
	const args = node.args;
	if (fn === "iif") {
		return truthy(evalExpr(args[0], ctx)) ? evalExpr(args[1], ctx) : evalExpr(args[2], ctx);
	}
	if (fn === "when") {
		for (let i = 0; i < args.length - 1; i += 2) {
			if (truthy(evalExpr(args[i], ctx))) {
				return evalExpr(args[i + 1], ctx);
			}
		}
		return evalExpr(args[args.length - 1], ctx);
	}
	// Decode
	const value = evalExpr(args[0], ctx);
	for (let i = 1; i < args.length - 1; i += 2) {
		if (value == evalExpr(args[i], ctx)) { // eslint-disable-line eqeqeq -- Arcade-style loose equality
			return evalExpr(args[i + 1], ctx);
		}
	}
	return evalExpr(args[args.length - 1], ctx);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function compileArcade(text) {
	if (typeof text !== "string" || text.trim() === "") {
		return null;
	}
	let ast;
	try {
		ast = parse(tokenize(text));
		validate(ast);
	} catch (err) {
		return null;
	}
	return {
		evaluate(props) {
			try {
				return execProgram(ast.body, { feature: props || {}, vars: {} });
			} catch (err) {
				return undefined;
			}
		},
	};
}
